var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AuthenticationReason, AuthenticationResult, AuthenticationStatus, ThreeDsStatus } from '@mc-alberta/types';
import getThreeDsBrowserData from '../../methods/getThreeDsBrowserData';
import { Adapter } from '../Adapter';
import { AuthenticationStrategy } from '../../types/enums/AuthenticationStrategy';
import { KpiOperation } from '../../types/enums/KpiOperation';
import { browserExchange, challenge } from './threeds';
import { LifecycleAwaitable, LifecycleAwaiting } from '../../decorators/LifecycleAwaiter';
import { HttpHeader } from '../../types/enums/http-header.enum';
import { VERIFICATION_METHOD } from '../../constants';
export class ThreeDsAdapter extends Adapter {
    constructor() {
        super(...arguments);
        this.strategy = AuthenticationStrategy.THREE_DS;
        this.supportedReasons = Object.keys(AuthenticationReason);
        this.verificationMethods = [VERIFICATION_METHOD.THREE_DS];
        this.selectedMethodIndex = 0;
        this.methodAttributes = {};
        /** Whether or not a challenge was fulfilled by the user. */
        this.isChallengeAttempted = false;
        /** Result of the pre-requisite "browser exchange" mechanism. */
        this.browserExchangeResult = ThreeDsStatus.Unknown;
        /**
         * Injects the 3DS challenge into the DOM element having the given selector.
         *
         * This resolves once the challenge is submitted by the user and validated by ACS.
         */
        this.provideChallenge = (challengeSelector) => __awaiter(this, void 0, void 0, function* () {
            try {
                this.kpi.start(KpiOperation.PROVIDE_AUTHENTICATION_CHALLENGE);
                yield challenge(this.threeDsOutputData, 600000, challengeSelector);
                this.authenticationStatus = AuthenticationStatus.PENDING;
                this.isChallengeAttempted = true;
            }
            catch (error) {
                if (error.message !== '3DS_TIMEOUT') {
                    return this.destroySession(error);
                }
                this.authenticationStatus = AuthenticationStatus.EXPIRED;
            }
            return this.getSession();
        });
        /**
         * Validates the 3DS authentication session.
         */
        this.validateSession = () => __awaiter(this, void 0, void 0, function* () {
            this.kpi.start(KpiOperation.COMPLETE_AUTHENTICATION);
            const session = yield this.refreshSession({}, {}, Object.assign({}, (this.authorization && {
                [HttpHeader.X_SRC_CARD_TOKEN]: this.authorization
            })));
            if (session.authenticationResult === AuthenticationResult.AUTHENTICATED) {
                this.isIdentityVerificationPerformed = this.isChallengeAttempted;
            }
            else if (session.authenticationStatus === AuthenticationStatus.COMPLETE) {
                this.authenticationStatus = AuthenticationStatus.CHALLENGE_FAILED;
            }
            return this.getSession();
        });
        /**
         * Performs the browser exchange mechanism.
         *
         * @returns the result of the browser exchange process (`Y` = Success, `N` = failure)
         */
        this.getBrowserExchangeResult = () => __awaiter(this, void 0, void 0, function* () {
            try {
                yield browserExchange(this.threeDsOutputData, 10000);
                return ThreeDsStatus.Success;
            }
            catch (error) {
                this.kpi.queueError(error);
            }
            return ThreeDsStatus.Failure;
        });
        this.setMethodAttributes = () => { };
    }
    /** ACS-specific 3DS data, including browser exchange and challenge data. */
    get threeDsOutputData() {
        var _a, _b, _c;
        return (_c = (_b = (_a = this.authenticationMethod) === null || _a === void 0 ? void 0 : _a.methodAttributes) === null || _b === void 0 ? void 0 : _b.threeDsData) === null || _c === void 0 ? void 0 : _c.threeDsOutputData;
    }
    /**
     * Preloads a 3DS session.
     */
    preloadSession() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.authenticationStatus !== AuthenticationStatus.PENDING) {
                return this.getSession();
            }
            this.kpi.start(KpiOperation.INITIATE_AUTHENTICATION);
            this.browserExchangeResult = yield this.getBrowserExchangeResult();
            return this.getSession();
        });
    }
    /**
     * Initializes a 3DS authentication session.
     */
    initializeSession({ authorization, isIdentityVerificationRequired }) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            this.authorization = authorization;
            this.isIdentityVerificationPerformed = false;
            this.isChallengeAttempted = false;
            if (this.browserExchangeResult === ThreeDsStatus.Unknown) {
                yield this.preloadSession();
            }
            const payload = {
                threeDsData: {
                    threeDsOutputData: (_b = (_a = this.authenticationMethod.methodAttributes) === null || _a === void 0 ? void 0 : _a.threeDsData) === null || _b === void 0 ? void 0 : _b.threeDsOutputData,
                    threeDsInputData: Object.assign(Object.assign({}, (_c = this.authenticationContext.dpaTransactionOptions) === null || _c === void 0 ? void 0 : _c.threeDsInputData), { challengeWindowSize: 'FULL', browser: Object.assign(Object.assign({}, getThreeDsBrowserData()), { threeDSCompInd: this.browserExchangeResult }), forceChallenge: isIdentityVerificationRequired })
                }
            };
            // first authenticate call to initialize the session and perform the browser exchange
            yield this.refreshSession(payload);
            // second authenticate call to determine: (a) challenge is needed, (b) frictionless success, or (c) some failure
            return this.refreshSession(payload);
        });
    }
    toJSON() {
        return Object.assign(Object.assign({}, super.toJSON()), { methodAttributes: this.methodAttributes });
    }
}
__decorate([
    LifecycleAwaitable,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], ThreeDsAdapter.prototype, "preloadSession", null);
__decorate([
    LifecycleAwaiting('preloadSession'),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", Promise)
], ThreeDsAdapter.prototype, "initializeSession", null);
